/**
 * useLocalStorage custom hook
 * Provides a way to persist state in localStorage with TypeScript support
 */
import { useState, useEffect } from 'react';

/**
 * Custom hook to manage state in localStorage
 * @param key - The localStorage key
 * @param initialValue - Initial value if no value exists in localStorage
 * @returns Tuple of [storedValue, setValue]
 */
export const useLocalStorage = <T>(
    key: string,
    initialValue: T
): [T, (value: T | ((val: T) => T)) => void] => {
    /**
     * State to store our value
     * Pass initial state function to useState so logic is only executed once
     */
    const [storedValue, setStoredValue] = useState<T>(() => {
        if (typeof window === 'undefined') {
            return initialValue;
        }

        try {
            // Get from local storage by key
            const item = window.localStorage.getItem(key);
            // Parse stored json or if none return initialValue
            return item ? (JSON.parse(item) as T) : initialValue;
        } catch (error) {
            // If error also return initialValue
            console.error(`Error reading localStorage key "${key}":`, error);
            return initialValue;
        }
    });

    /**
     * Return a wrapped version of useState's setter function that
     * persists the new value to localStorage.
     */
    const setValue = (value: T | ((val: T) => T)): void => {
        try {
            // Allow value to be a function so we have same API as useState
            const valueToStore = value instanceof Function ? value(storedValue) : value;

            // Save state
            setStoredValue(valueToStore);

            // Save to local storage
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(key, JSON.stringify(valueToStore));
            }
        } catch (error) {
            // A more advanced implementation would handle the error case
            console.error(`Error setting localStorage key "${key}":`, error);
        }
    };

    /**
     * Listen for changes to this key from other tabs/windows
     */
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent): void => {
            if (e.key === key && e.newValue !== null) {
                try {
                    setStoredValue(JSON.parse(e.newValue) as T);
                } catch (error) {
                    console.error(`Error parsing localStorage value for key "${key}":`, error);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [key]);

    return [storedValue, setValue];
};
